import {
  UnitedStateCode,
  CustomerApplication,
  CustomerApplicationDeepPartial,
  BrokerRecognizedEmployer,
} from "@goodfill/domain-customer-application-types";
import { DefaultValues } from "react-hook-form";
import { serializePhone } from "../../../form/zod/formPhone";
import { DOCUMENT_UPLOAD_TYPE } from "../../backendApi/lambdas/individualApplication/uploadDocumentForApplicationForm";
import { ValidStep1SignUpState } from "./form/steps/Step1SignUp/step1SignUpState";
import {
  CustomerApplicationFormState,
  isEmploymentDetailRequired,
  selectedRecognizedEmployerTransform,
} from "./formState/formState";

export function mapCustomerApplicationApiToStep1(
  apiApplication: CustomerApplicationDeepPartial<CustomerApplication>,
): DefaultValues<ValidStep1SignUpState> {
  return {
    firstName: apiApplication?.personalInformation?.name?.first,
    lastName: apiApplication?.personalInformation?.name?.last,
    email: apiApplication?.personalInformation?.email,
    phone1: serializePhone(
      apiApplication?.personalInformation?.phones?.[0]?.countryCallingCode ?? "",
      apiApplication?.personalInformation?.phones?.[0]?.number ?? "",
    ),
  };
}

export function mapCustomerApplicationApiToState(
  apiApplication: CustomerApplicationDeepPartial<CustomerApplication>,
): DefaultValues<CustomerApplicationFormState> {
  const proofOfIdentityDocuments = apiApplication?.documents?.filter(
    (document) => document && (document.type as DOCUMENT_UPLOAD_TYPE) === "ProofOfIdentity",
  );

  const affiliationDetails =
    (apiApplication?.regulatoryInformation?.affiliation?.recognizedEmployerDetails ||
      apiApplication?.regulatoryInformation?.affiliation?.details) ??
    null;

  const isSameMailingAddress = Boolean(!apiApplication?.personalInformation?.mailingAddress);

  const brokerRecognizedEmployer = selectedRecognizedEmployerTransform.toState(
    // Hack: cast to stop deep-partial to go to much deep
    apiApplication?.regulatoryInformation?.affiliation?.recognizedEmployerDetails?.brokerRecognizedEmployer as
      | BrokerRecognizedEmployer
      | undefined
      | null,
  );

  return {
    // step1: // separated to its own form
    step2: {
      dateOfBirth: apiApplication?.personalInformation?.dateOfBirth,
      maritalStatus: apiApplication?.personalInformation?.maritalStatus,
      numberOfDependents: apiApplication?.personalInformation?.numberOfDependents,

      address: {
        street1: apiApplication?.personalInformation?.residence?.street1,
        street2: apiApplication?.personalInformation?.residence?.street2 ?? "",
        city: apiApplication?.personalInformation?.residence?.city,
        state: apiApplication?.personalInformation?.residence?.state as UnitedStateCode,
        zipCode: apiApplication?.personalInformation?.residence?.zipCode,
      },

      sameMailingAddress: isSameMailingAddress,
      mailingAddress: isSameMailingAddress
        ? null
        : {
            street1: apiApplication?.personalInformation?.mailingAddress?.street1 ?? "",
            street2: apiApplication?.personalInformation?.mailingAddress?.street2 ?? "",
            city: apiApplication?.personalInformation?.mailingAddress?.city ?? "",
            state: apiApplication?.personalInformation?.mailingAddress?.state as UnitedStateCode,
            zipCode: apiApplication?.personalInformation?.mailingAddress?.zipCode ?? "",
          },

      issuingCountryOfIdentification:
        apiApplication.personalInformation?.issuingCountryOfIdentificationDocument?.countryIsoCode,
      proofOfAddressFileId:
        apiApplication?.documents?.find(
          (document) => document && (document.type as DOCUMENT_UPLOAD_TYPE) === "ProofOfAddress",
        )?.documentId ?? undefined,
      proofOfIdentityFileFrontId: proofOfIdentityDocuments?.[0]?.documentId ?? undefined,
      proofOfIdentityFileBackId: proofOfIdentityDocuments?.[1]?.documentId ?? undefined,
    },
    step3: {
      countryOfBirth: apiApplication?.personalInformation?.countryOfBirth?.countryIsoCode,
      socialSecurityNumber: apiApplication?.personalInformation?.socialSecurityNumber,
      countryOfCitizenship: apiApplication?.personalInformation?.citizenships?.[0]?.countryIsoCode,
      ssnAcknowledgement: false, // intentionally to force user to re-check it every time
      ssnWithholdingAcknowledgement: false, // intentionally to force user to re-check it every time
      individualApplyingAcknowledgement: false, // intentionally to force user to re-check it every time
      trueAndCorrectSignature: "", // intentionally to force user to re-check it every time
    },
    step4: {
      employmentStatus: apiApplication?.personalInformation?.employment?.status,
      employment: isEmploymentDetailRequired(apiApplication?.personalInformation?.employment?.status ?? null)
        ? {
            employer: {
              name: apiApplication?.personalInformation?.employment?.details?.employer ?? "",
              country:
                apiApplication?.personalInformation?.employment?.details?.employerCountry?.countryIsoCode ?? undefined,
              abroadExplanation:
                apiApplication?.personalInformation?.employment?.details?.employerCountryAbroadExplanation ?? "",
            },
            occupation: apiApplication?.personalInformation?.employment?.details?.occupation
              ? {
                  occupation: apiApplication.personalInformation.employment.details?.occupation,
                  business: apiApplication.personalInformation.employment.details?.employerBusiness,
                }
              : undefined,
          }
        : null,
    },
    step5: {
      investmentObjectives: apiApplication?.accountInformation?.investmentObjectives,
      annualNetIncome: Number(apiApplication?.financialInformation?.annualNetIncome?.amount),
      netWorth: Number(apiApplication?.financialInformation?.netWorth?.amount),
      liquidNetWorth: Number(apiApplication?.financialInformation?.liquidNetWorth?.amount),

      sourcesOfWealth:
        apiApplication?.financialInformation?.sourcesOfWealth?.map((value) => ({
          description: value?.description ?? "",
          frequency: value?.frequency,
          isUsedForFunds: value?.isUsedForFunds,
          // WARNING: there is `type` property, but it is internal type of Zod
          // make sure you are mapping it on `wealthType`
          wealthType: value?.type,
        })) ?? [],
    },
    step6: {
      tradesPerYearBonds: apiApplication?.financialInformation?.investmentExperience?.bonds?.tradesPerYear,
      bondsExperienceLevel: apiApplication?.financialInformation?.investmentExperience?.bonds?.knowledgeLevel,
      yearsExperienceBonds: apiApplication?.financialInformation?.investmentExperience?.bonds?.yearsTrading,
    },
    step7: {
      isBroker: apiApplication?.regulatoryInformation?.affiliation?.isAffiliated,
      broker: apiApplication?.regulatoryInformation?.affiliation?.isAffiliated
        ? {
            duplicateStatementRequired: affiliationDetails?.isDuplicateStatementRequired ?? undefined,
            brokerAffiliationRelationship: affiliationDetails?.relationship ?? undefined,
            brokerName: affiliationDetails?.affiliatedPersonFullName ?? "",

            // If `brokerRecognizedEmployer` use it
            selectedRecognizedEmployer: brokerRecognizedEmployer,

            // If not `brokerRecognizedEmployer` the broker company is required
            brokerCompany: brokerRecognizedEmployer
              ? null
              : {
                  name: apiApplication.regulatoryInformation.affiliation.details?.company?.name ?? "",
                  email: apiApplication.regulatoryInformation.affiliation.details?.company?.email ?? "",
                  phone: serializePhone(
                    apiApplication.regulatoryInformation.affiliation.details?.company?.phone?.countryCallingCode ?? "",
                    apiApplication.regulatoryInformation.affiliation.details?.company?.phone?.number ?? "",
                  ),
                  address: {
                    street1: apiApplication.regulatoryInformation.affiliation.details?.company?.address?.street1 ?? "",
                    street2: apiApplication.regulatoryInformation.affiliation.details?.company?.address?.street2 ?? "",
                    state:
                      (apiApplication.regulatoryInformation.affiliation.details?.company?.address
                        ?.state as UnitedStateCode) || null,
                    city: apiApplication.regulatoryInformation.affiliation.details?.company?.address?.city ?? "",
                    zipCode: apiApplication.regulatoryInformation.affiliation.details?.company?.address?.zipCode ?? "",
                  },
                },
          }
        : null,

      isEmployeeOfPubliclyTradedCompany:
        apiApplication?.regulatoryInformation?.employeeOfPubliclyTradedCompany?.isEmployeeOfPubliclyTradedCompany ===
        undefined
          ? undefined
          : apiApplication?.regulatoryInformation?.employeeOfPubliclyTradedCompany.isEmployeeOfPubliclyTradedCompany,
      employeeOfPubliclyTradedCompanyStockSymbols:
        apiApplication?.regulatoryInformation?.employeeOfPubliclyTradedCompany?.companyStockSymbols === undefined
          ? []
          : apiApplication?.regulatoryInformation.employeeOfPubliclyTradedCompany.companyStockSymbols,

      hasControlOfPubliclyTradedCompany:
        apiApplication?.regulatoryInformation?.controlOfPubliclyTradedCompany?.hasControlOfPubliclyTradedCompany ===
        undefined
          ? undefined
          : apiApplication?.regulatoryInformation.controlOfPubliclyTradedCompany.hasControlOfPubliclyTradedCompany,
      controlOfPubliclyTradedCompanyStockSymbols:
        apiApplication?.regulatoryInformation?.controlOfPubliclyTradedCompany?.companyStockSymbols === undefined
          ? []
          : apiApplication?.regulatoryInformation?.controlOfPubliclyTradedCompany.companyStockSymbols,
    },
    step8: {
      allowBondTrading: apiApplication?.accountInformation?.permissions?.allowBondTrading,
      allowChangingAccountInformationAfterUserInstruction:
        apiApplication?.accountInformation?.permissions?.allowChangingAccountInformationAfterUserInstruction,
      allowTransmittingReceivedOrders:
        apiApplication?.accountInformation?.permissions?.allowTransmittingReceivedOrders,
      allowConfirmAccountInformation:
        apiApplication?.accountInformation?.permissions?.allowConfirmingAccountInformation,
      allowRespondingToTenders: apiApplication?.accountInformation?.permissions?.allowRespondingToTenders,
      allowRespondingToPutOptionsContingentUponCorporateAction:
        apiApplication?.accountInformation?.permissions?.allowRespondingToPutOptionsContingentUponCorporateAction,
      allowRespondingToPutOptionsNotContingentUponCorporateAction:
        apiApplication?.accountInformation?.permissions?.allowRespondingToPutOptionsNotContingentUponCorporateAction,
      allowOtherElectionsByBondHolders:
        apiApplication?.accountInformation?.permissions?.allowOtherElectionsByBondHolders,

      isInteractiveBrokersTermsConfirmed: false, // intentionally to force user to re-check it every time
      signature: "", // intentionally to force user to re-check it every time
    },
  };
}
